import React from 'react';
import Modal from './Modal';
import { Button } from './Button';
import { RiErrorWarningLine, RiQuestionLine, RiCheckLine, RiCloseLine } from 'react-icons/ri';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isDanger?: boolean;
  isLoading?: boolean;
  /**
   * ส่งต่อให้ ui/Modal (default true) — กล่องยืนยันของ `useUnsavedChanges` ต้องส่ง false: ตัวกันจัดการช่อง history เอง
   * (ดันช่องกัน · ยืนยันออก = go(-2)) ถ้า Modal ดันช่องซ้อนอีกชั้น กด "อยู่ต่อ" แล้ว Modal ถอยกินช่องของตัวเอง →
   * popstate ถึงตัวกัน → ตัวกันเข้าใจว่าผู้ใช้กด Back → เปิดกล่องซ้ำไม่รู้จบ (มติ 30 ก.ย. 2026 ข้อ 7 · E2E)
   */
  pushHistoryOnOpen?: boolean;
}

const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'ยืนยัน',
  cancelText = 'ยกเลิก',
  isDanger = false,
  isLoading = false,
  pushHistoryOnOpen = true,
}) => {
  // BUG-S1197-001 fix: Synchronous click guard — prevents double-/triple-click race
  // before React `isLoading` state can propagate from parent. The `isLoading` prop
  // (driven by async mutation) takes a microtask to flip; meanwhile sync click handlers
  // already fire on each click. This ref blocks 2nd/3rd clicks at the DOM event level.
  const inFlightRef = React.useRef(false);
  // KD3 S2490 (NFR-MD-013): กล่องยืนยันต้องเป็น alertdialog + ผูกเนื้อความ — เดิม role=dialog ไม่มี describedby
  // screen reader อ่านแค่หัวข้อ "ยืนยันการปิดโครงการ" ไม่รู้ว่ามีการเช่ากี่รายการ/ผลกระทบอะไร
  const messageId = `confirm-message-${React.useId().replace(/:/g, '')}`;

  // Reset guard when dialog closes (next open is fresh).
  React.useEffect(() => {
    if (!isOpen) inFlightRef.current = false;
  }, [isOpen]);

  const handleConfirmGuarded = React.useCallback(() => {
    if (inFlightRef.current) return; // synchronous race-loser drop
    inFlightRef.current = true;
    onConfirm();
  }, [onConfirm]);

  // ฟุตเตอร์ของ Modal
  const footer = (
    <>
      <Button
        variant="secondary"
        size="md"
        onClick={onClose}
        disabled={isLoading || inFlightRef.current}
        icon={<RiCloseLine />}
      >
        {cancelText}
      </Button>
      <Button
        variant={isDanger ? 'danger' : 'primary'}
        size="md"
        onClick={handleConfirmGuarded}
        loading={isLoading || inFlightRef.current}
        loadingText="กำลังดำเนินการ..."
        icon={!isLoading ? <RiCheckLine /> : undefined}
      >
        {confirmText}
      </Button>
    </>
  );

  // เลือก Icon ตาม isDanger
  const icon = isDanger ? (
    <RiErrorWarningLine className="h-6 w-6 text-danger-500" />
  ) : (
    <RiQuestionLine className="h-6 w-6 text-info-500" />
  );

  // กำหนดสีขอบและพื้นหลังตาม isDanger
  const borderColor = isDanger ? 'border-danger-500' : 'border-info-500';
  const bgColor = isDanger ? 'bg-danger-50' : 'bg-info-50';
  const textColor = isDanger ? 'text-danger-700' : 'text-info-700';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={footer}
      icon={icon}
      closeOnClickOutside={!isLoading}
      closeOnEsc={!isLoading}
      role="alertdialog"
      ariaDescribedBy={messageId}
      pushHistoryOnOpen={pushHistoryOnOpen}
    >
      <div className={`p-4 rounded-md ${bgColor} border-l-4 ${borderColor}`}>
        <div id={messageId} className={`${textColor} whitespace-pre-line [overflow-wrap:anywhere]`}>
          {message}
        </div>
      </div>
    </Modal>
  );
};

export default ConfirmModal;